// src/pages/admin/AdminLibrary.tsx
//
// Full library management for:
//   admin, committee_chair, committee_member, portal_manager — full CRUD
//   regional_rep / mentee — no access (blocked by router guard)
//
// Three bugs fixed from v1:
//   1. isPublished added to LibraryContent type and formatContent response
//   2. Admin query passes includeAll=true so drafts appear in the grid
//   3. YouTube preview uses a stable key + correct aspect wrapper so Plyr mounts once per ID

import React, { useState, useMemo, useCallback, useEffect } from "react";

import {
  Plus,
  Search,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  X,
  Check,
  AlertCircle,
  Clock,
  BookOpen,
  Play,
  Headphones,
  FileText,
  ClipboardList,
  ExternalLink,
  Loader2,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetLibraryContentQuery,
  useCreateContentMutation,
  useUpdateContentMutation,
  useDeleteContentMutation,
} from "../../app/api/libraryApi";
import type {
  LibraryContent,
  ContentType,
  ReadingItem,
} from "../../app/api/libraryApi";
import type { UserRole } from "../../app/api/authApi";
import type { QuarterKey } from "../../app/api/programmeApi";
import PlyrPlayer from "../../components/common/PlyrPlayer";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const CONTENT_ROLES: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "co",
  "cyp",
];

const QUARTERS: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

const CONTENT_TYPE_META: {
  value: ContentType;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: "keynote",
    label: "Keynote",
    icon: <Play size={14} />,
    description: "Mantle Session recording (YouTube)",
  },
  {
    value: "panel",
    label: "Panel",
    icon: <Headphones size={14} />,
    description: "Leadership Lab panel (YouTube)",
  },
  {
    value: "reading",
    label: "Reading List",
    icon: <BookOpen size={14} />,
    description: "Curated books & articles",
  },
  {
    value: "resource",
    label: "Resource",
    icon: <FileText size={14} />,
    description: "Slides, handouts, documents",
  },
  {
    value: "task",
    label: "Task Brief",
    icon: <ClipboardList size={14} />,
    description: "Leadership task document",
  },
];

const TYPE_CHIP: Record<ContentType, string> = {
  keynote: "bg-green-100  text-green-700",
  panel: "bg-blue-100   text-blue-700",
  reading: "bg-purple-100 text-purple-700",
  resource: "bg-orange-100 text-orange-700",
  task: "bg-rose-100   text-rose-700",
};

const isVideoType = (t: ContentType) => t === "keynote" || t === "panel";

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

/* ─────────────────────────────────────────────────────────────────
   FORM STATE
───────────────────────────────────────────────────────────────── */

interface FormState {
  contentType: ContentType;
  quarter: QuarterKey;
  title: string;
  description: string;
  durationMinutes: string;
  tags: string;
  videoUrl: string;
  thumbnailUrl: string;
  fileUrl: string;
  fileName: string;
  isDownloadable: boolean;
  isPublished: boolean;
  readingItems: (Partial<ReadingItem> & { _tempId: string })[];
  chapterMarkers: { _tempId: string; timeSeconds: string; label: string }[];
}

const EMPTY_FORM: FormState = {
  contentType: "keynote",
  quarter: "Q1",
  title: "",
  description: "",
  durationMinutes: "",
  tags: "",
  videoUrl: "",
  thumbnailUrl: "",
  fileUrl: "",
  fileName: "",
  isDownloadable: false,
  isPublished: false,
  readingItems: [],
  chapterMarkers: [],
};

const itemToForm = (c: LibraryContent): FormState => ({
  contentType: c.contentType,
  quarter: c.quarter,
  title: c.title,
  description: c.description ?? "",
  durationMinutes: c.durationMinutes ? String(c.durationMinutes) : "",
  tags: c.tags?.join(", ") ?? "",
  videoUrl: c.videoUrl ?? "",
  thumbnailUrl: c.thumbnailUrl ?? "",
  fileUrl: c.fileUrl ?? "",
  fileName: c.fileName ?? "",
  isDownloadable: c.isDownloadable,
  isPublished: c.isPublished,
  readingItems: (c.readingItems ?? []).map((r) => ({
    ...r,
    _tempId: r._id ?? Math.random().toString(36),
  })),
  chapterMarkers: (c.chapterMarkers ?? []).map((m) => ({
    _tempId: Math.random().toString(36),
    timeSeconds: String(m.timeSeconds),
    label: m.label,
  })),
});

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */

// const Bone: React.FC<{ className?: string }> = ({ c = "" }) => (
//   <div className={`animate-pulse rounded-lg bg-gray-100 ${c}`} />
// );

const FL: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (p) => (
  <input
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition disabled:bg-gray-50 disabled:text-gray-400 ${p.className ?? ""}`}
  />
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (
  p,
) => (
  <textarea
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition resize-none ${p.className ?? ""}`}
  />
);

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (p) => (
  <select
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition ${p.className ?? ""}`}
  />
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  sub?: string;
}> = ({ checked, onChange, label, sub }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <div className="relative mt-0.5 shrink-0">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className={`w-9 h-5 rounded-full transition-colors ${checked ? "bg-purple-600" : "bg-gray-200"}`}
      />
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}
      />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </label>
);

/* ─────────────────────────────────────────────────────────────────
   YOUTUBE PREVIEW
   Key is the videoId so PlyrPlayer fully unmounts and remounts
   whenever the URL changes — this prevents Plyr from initialising
   on stale DOM from a previous video.
───────────────────────────────────────────────────────────────── */

const YouTubePreview: React.FC<{ videoUrl: string }> = ({ videoUrl }) => {
  const videoId = extractYouTubeId(videoUrl);

  if (!videoId) {
    return (
      <div className="flex flex-col items-center justify-center h-36 rounded-xl bg-gray-50 border border-dashed border-gray-200 text-gray-400 gap-2">
        <Play size={22} className="opacity-30" />
        <p className="text-xs">Paste a YouTube URL above to preview</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 bg-black">
      {/* aspect-video wrapper — Plyr needs a sized container */}
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <div className="absolute inset-0">
          <PlyrPlayer
            key={videoId} // remount when ID changes
            provider="youtube"
            embedId={videoId}
            options={{
              controls: [
                "play",
                "progress",
                "current-time",
                "mute",
                "volume",
                "fullscreen",
              ],
              clickToPlay: true,
              youtube: { noCookie: true, rel: 0, modestbranding: 1 },
            }}
          />
        </div>
      </div>
      <div className="px-3 py-2 bg-gray-900 flex items-center gap-2">
        <ExternalLink size={11} className="text-gray-400 shrink-0" />
        <span className="text-[11px] text-gray-400 font-mono truncate">
          {videoId}
        </span>
        <a
          href={`https://youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noreferrer"
          className="ml-auto shrink-0 text-[11px] text-purple-400 hover:text-purple-300 transition-colors"
        >
          Open ↗
        </a>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   CONTENT FORM
───────────────────────────────────────────────────────────────── */

interface FormProps {
  initial: FormState;
  isEditing: boolean;
  isSaving: boolean;
  apiError: string | null;
  onSave: (f: FormState) => void;
  onCancel: () => void;
}

const ContentForm: React.FC<FormProps> = ({
  initial,
  isEditing,
  isSaving,
  apiError,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState<FormState>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setForm(initial);
    setFieldErrors({});
  }, [initial]);

  const set = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) =>
      setForm((p) => ({ ...p, [key]: value })),
    [],
  );

  // ── Reading helpers ──────────────────────────────────────────
  const addRI = () =>
    set("readingItems", [
      ...form.readingItems,
      {
        _tempId: Math.random().toString(36),
        title: "",
        author: "",
        type: "book",
        externalUrl: "",
        isPrimary: false,
      },
    ]);
  const updateRI = (id: string, k: string, v: unknown) =>
    set(
      "readingItems",
      form.readingItems.map((r) => (r._tempId === id ? { ...r, [k]: v } : r)),
    );
  const removeRI = (id: string) =>
    set(
      "readingItems",
      form.readingItems.filter((r) => r._tempId !== id),
    );

  // ── Chapter helpers ──────────────────────────────────────────
  const addCM = () =>
    set("chapterMarkers", [
      ...form.chapterMarkers,
      { _tempId: Math.random().toString(36), timeSeconds: "", label: "" },
    ]);
  const updateCM = (id: string, k: "timeSeconds" | "label", v: string) =>
    set(
      "chapterMarkers",
      form.chapterMarkers.map((m) => (m._tempId === id ? { ...m, [k]: v } : m)),
    );
  const removeCM = (id: string) =>
    set(
      "chapterMarkers",
      form.chapterMarkers.filter((m) => m._tempId !== id),
    );

  // ── Validate ─────────────────────────────────────────────────
  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Title is required.";
    if (!form.quarter) e.quarter = "Quarter is required.";
    if (isVideoType(form.contentType)) {
      if (!form.videoUrl.trim()) e.videoUrl = "YouTube URL is required.";
      else if (!extractYouTubeId(form.videoUrl))
        e.videoUrl = "Could not parse a YouTube video ID from this URL.";
    }
    if (form.contentType === "reading" && form.readingItems.length === 0)
      e.readingItems = "Add at least one item.";
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  const selectedMeta = CONTENT_TYPE_META.find(
    (t) => t.value === form.contentType,
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Type selector */}
      <div>
        <FL required>Content type</FL>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-1">
          {CONTENT_TYPE_META.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => set("contentType", t.value)}
              className={`flex flex-col items-center gap-1 py-3 px-2 rounded-xl border text-[11px] font-semibold transition-all
                ${
                  form.contentType === t.value
                    ? "border-purple-500 bg-purple-50 text-purple-700 shadow-sm"
                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                }`}
            >
              <span
                className={
                  form.contentType === t.value
                    ? "text-purple-600"
                    : "text-gray-400"
                }
              >
                {t.icon}
              </span>
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-400">{selectedMeta?.description}</p>
      </div>

      {/* Quarter + Duration row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FL required>Quarter</FL>
          <Sel
            value={form.quarter}
            onChange={(e) => set("quarter", e.target.value as QuarterKey)}
          >
            {QUARTERS.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </Sel>
          {fieldErrors.quarter && (
            <p className="text-[11px] text-red-500 mt-1">
              {fieldErrors.quarter}
            </p>
          )}
        </div>
        {isVideoType(form.contentType) && (
          <div>
            <FL>Duration (minutes)</FL>
            <Input
              type="number"
              min={0}
              placeholder="e.g. 70"
              value={form.durationMinutes}
              onChange={(e) => set("durationMinutes", e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Title */}
      <div>
        <FL required>Title</FL>
        <Input
          placeholder={
            form.contentType === "keynote"
              ? "Mantle Session — Q2 Recording"
              : form.contentType === "panel"
                ? "Leadership Lab — Governance Dialogue"
                : form.contentType === "reading"
                  ? "Overseer's Q2 Reading List"
                  : form.contentType === "resource"
                    ? "Q2 Event Slides & Handouts"
                    : "Q2 Task Brief — Governance Audit"
          }
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          maxLength={200}
        />
        {fieldErrors.title && (
          <p className="text-[11px] text-red-500 mt-1">{fieldErrors.title}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <FL>Description</FL>
        <Textarea
          rows={3}
          placeholder="Brief description shown on the library card…"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          maxLength={1000}
        />
        <p className="text-right text-[11px] text-gray-400 mt-0.5">
          {form.description.length}/1000
        </p>
      </div>

      {/* Tags */}
      <div>
        <FL>Tags</FL>
        <Input
          placeholder="Governance, Ethics, Leadership  (comma-separated)"
          value={form.tags}
          onChange={(e) => set("tags", e.target.value)}
        />
      </div>

      {/* ── VIDEO SECTION ──────────────────────────────────────── */}
      {isVideoType(form.contentType) && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
            YouTube video
          </p>

          {/* URL */}
          <div>
            <FL required>YouTube URL</FL>
            <Input
              placeholder="https://www.youtube.com/watch?v=…"
              value={form.videoUrl}
              onChange={(e) => set("videoUrl", e.target.value)}
            />
            {fieldErrors.videoUrl ? (
              <p className="text-[11px] text-red-500 mt-1">
                {fieldErrors.videoUrl}
              </p>
            ) : (
              extractYouTubeId(form.videoUrl) && (
                <p className="text-[11px] text-green-600 mt-1 flex items-center gap-1">
                  <Check size={10} /> ID:{" "}
                  <span className="font-mono">
                    {extractYouTubeId(form.videoUrl)}
                  </span>
                </p>
              )
            )}
          </div>

          {/* Live preview — mounts Plyr on the video ID */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-2">
              Preview
            </p>
            <YouTubePreview videoUrl={form.videoUrl} />
          </div>

          {/* Thumbnail override */}
          <div>
            <FL>
              Custom thumbnail URL{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </FL>
            <Input
              placeholder="https://…/thumb.jpg"
              value={form.thumbnailUrl}
              onChange={(e) => set("thumbnailUrl", e.target.value)}
            />
          </div>

          {/* Chapter markers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                Chapter markers
              </p>
              <button
                type="button"
                onClick={addCM}
                className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
              >
                <Plus size={11} /> Add
              </button>
            </div>
            {form.chapterMarkers.length === 0 ? (
              <p className="text-[11px] text-gray-400">
                No markers yet. Add timestamps to help viewers navigate.
              </p>
            ) : (
              <div className="space-y-2">
                {form.chapterMarkers.map((m) => (
                  <div key={m._tempId} className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      placeholder="Seconds"
                      value={m.timeSeconds}
                      onChange={(e) =>
                        updateCM(m._tempId, "timeSeconds", e.target.value)
                      }
                      className="w-28 shrink-0"
                    />
                    <Input
                      placeholder="e.g. The cost of integrity"
                      value={m.label}
                      onChange={(e) =>
                        updateCM(m._tempId, "label", e.target.value)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeCM(m._tempId)}
                      className="shrink-0 p-1 text-gray-400 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── READING SECTION ────────────────────────────────────── */}
      {form.contentType === "reading" && (
        <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
              Reading items
            </p>
            <button
              type="button"
              onClick={addRI}
              className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
            >
              <Plus size={11} /> Add item
            </button>
          </div>
          {fieldErrors.readingItems && (
            <p className="text-[11px] text-red-500">
              {fieldErrors.readingItems}
            </p>
          )}
          {form.readingItems.length === 0 && (
            <p className="text-[11px] text-gray-400">
              Add books, articles, or other resources to this list.
            </p>
          )}
          <div className="space-y-3">
            {form.readingItems.map((item, idx) => (
              <div
                key={item._tempId}
                className="bg-white rounded-lg border border-gray-200 p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    Item {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeRI(item._tempId!)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <FL required>Title</FL>
                    <Input
                      placeholder="Book or article title"
                      value={item.title ?? ""}
                      onChange={(e) =>
                        updateRI(item._tempId!, "title", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <FL>Author</FL>
                    <Input
                      placeholder="Author name"
                      value={item.author ?? ""}
                      onChange={(e) =>
                        updateRI(item._tempId!, "author", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <FL>Type</FL>
                    <Sel
                      value={item.type ?? "book"}
                      onChange={(e) =>
                        updateRI(item._tempId!, "type", e.target.value)
                      }
                    >
                      {["book", "article", "video", "podcast", "other"].map(
                        (t) => (
                          <option key={t} value={t}>
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </option>
                        ),
                      )}
                    </Sel>
                  </div>
                  <div>
                    <FL>Link</FL>
                    <Input
                      placeholder="https://…"
                      value={item.externalUrl ?? ""}
                      onChange={(e) =>
                        updateRI(item._tempId!, "externalUrl", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div>
                  <FL>
                    Why recommended{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </FL>
                  <Input
                    placeholder="Why the Overseer recommends this…"
                    value={item.description ?? ""}
                    onChange={(e) =>
                      updateRI(item._tempId!, "description", e.target.value)
                    }
                  />
                </div>
                <Toggle
                  checked={item.isPrimary ?? false}
                  onChange={(v) => updateRI(item._tempId!, "isPrimary", v)}
                  label="Primary pick"
                  sub="Overseer's top recommendation this quarter"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FILE SECTION (resource / task) ─────────────────────── */}
      {(form.contentType === "resource" || form.contentType === "task") && (
        <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
            File
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <FL>File URL</FL>
              <Input
                placeholder="https://… (S3, Drive, etc.)"
                value={form.fileUrl}
                onChange={(e) => set("fileUrl", e.target.value)}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Paste a publicly accessible URL.
              </p>
            </div>
            <div>
              <FL>Display name</FL>
              <Input
                placeholder="Q2_Slides.pdf"
                value={form.fileName}
                onChange={(e) => set("fileName", e.target.value)}
              />
            </div>
          </div>
          <Toggle
            checked={form.isDownloadable}
            onChange={(v) => set("isDownloadable", v)}
            label="Downloadable"
            sub="Members can download this file"
          />
        </div>
      )}

      {/* ── PUBLISH SETTINGS ───────────────────────────────────── */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">
          Publish settings
        </p>
        <Toggle
          checked={form.isPublished}
          onChange={(v) => set("isPublished", v)}
          label="Publish immediately"
          sub="Visible to all members. Leave off to save as a draft."
        />
      </div>

      {/* API error */}
      {apiError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
          <AlertCircle size={13} className="shrink-0" />
          {apiError}
        </div>
      )}

      {/* Form actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all shadow-sm disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Check size={14} />{" "}
              {isEditing ? "Save changes" : "Create content"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/* ─────────────────────────────────────────────────────────────────
   CONTENT CARD
───────────────────────────────────────────────────────────────── */

interface CardProps {
  item: LibraryContent;
  onEdit: (i: LibraryContent) => void;
  onDelete: (i: LibraryContent) => void;
  onTogglePublish: (i: LibraryContent) => void;
  canEdit: boolean;
}

const ContentCard: React.FC<CardProps> = ({
  item,
  onEdit,
  onDelete,
  onTogglePublish,
  canEdit,
}) => {
  const chip = TYPE_CHIP[item.contentType];
  const videoId = isVideoType(item.contentType)
    ? extractYouTubeId(item.videoUrl ?? "")
    : null;
  const thumb = videoId
    ? (item.thumbnailUrl ??
      `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`)
    : null;

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all duration-150
      ${item.isPublished ? "border-gray-100" : "border-dashed border-amber-200"}`}
    >
      {/* Thumbnail */}
      {thumb ? (
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <img
            src={thumb}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
            <div className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
              <Play size={15} className="text-gray-900 ml-0.5" />
            </div>
          </div>
          <span
            className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${chip}`}
          >
            {item.contentType}
          </span>
          {item.durationLabel && (
            <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
              {item.durationLabel}
            </span>
          )}
        </div>
      ) : (
        <div className={`h-1.5 w-full ${chip.split(" ")[0]}`} />
      )}

      <div className="p-4 space-y-2">
        {/* Badges */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {!thumb && (
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${chip}`}
              >
                {item.contentType}
              </span>
            )}
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
              ${item.isPublished ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
            >
              {item.isPublished ? "Published" : "Draft"}
            </span>
            {item.isNew && item.isPublished && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase tracking-wide">
                New
              </span>
            )}
          </div>
          <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full shrink-0">
            {item.quarter}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
          {item.title}
        </h3>

        {item.description && (
          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
            {item.description}
          </p>
        )}

        {item.contentType === "reading" &&
          (item.readingItems?.length ?? 0) > 0 && (
            <p className="text-[11px] text-purple-600 flex items-center gap-1">
              <BookOpen size={10} /> {item.readingItems.length} item
              {item.readingItems.length !== 1 ? "s" : ""}
            </p>
          )}

        {/* Tags */}
        {(item.tags?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium"
              >
                {t}
              </span>
            ))}
            {item.tags.length > 3 && (
              <span className="text-[10px] text-gray-400">
                +{item.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
          <p className="text-[10px] text-gray-400">
            {new Date(item.createdAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </p>
          {canEdit && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onTogglePublish(item)}
                title={item.isPublished ? "Unpublish" : "Publish"}
                className={`p-1.5 rounded-lg transition-colors
                  ${item.isPublished ? "text-green-600 hover:bg-green-50" : "text-amber-600 hover:bg-amber-50"}`}
              >
                {item.isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              <button
                onClick={() => onEdit(item)}
                title="Edit"
                className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              >
                <Pencil size={14} />
              </button>
              <button
                onClick={() => onDelete(item)}
                title="Delete"
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   DELETE CONFIRM
───────────────────────────────────────────────────────────────── */

const DeleteConfirm: React.FC<{
  item: LibraryContent;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ item, isLoading, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
          <Trash2 size={18} className="text-red-600" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Delete content?</h3>
          <p className="text-xs text-gray-500">This cannot be undone.</p>
        </div>
      </div>
      <div className="bg-gray-50 rounded-xl px-4 py-3 mb-5">
        <p className="text-sm font-semibold text-gray-800 line-clamp-2">
          {item.title}
        </p>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {item.quarter} · {item.contentType}
        </p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {isLoading ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const AdminLibrary: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "portal_manager") as UserRole;
  const canEdit = CONTENT_ROLES.includes(role);

  const [quarterFilter, setQuarterFilter] = useState<QuarterKey | "all">("all");
  const [typeFilter, setTypeFilter] = useState<ContentType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");
  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<LibraryContent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryContent | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // ── KEY FIX #3: Pass includeAll=true so drafts appear for content-role admins
  const { data: libraryRes, isLoading } = useGetLibraryContentQuery({
    ...(quarterFilter !== "all" ? { quarter: quarterFilter } : {}),
    ...(canEdit ? { includeAll: true } : {}),
  });

  const [createContent, { isLoading: isCreating }] = useCreateContentMutation();
  const [updateContent, { isLoading: isUpdating }] = useUpdateContentMutation();
  const [deleteContent, { isLoading: isDeleting }] = useDeleteContentMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allItems = libraryRes?.data ?? [];

  const filtered = useMemo(() => {
    let items = allItems;
    if (typeFilter !== "all")
      items = items.filter((i) => i.contentType === typeFilter);
    if (statusFilter === "published")
      items = items.filter((i) => i.isPublished);
    if (statusFilter === "draft") items = items.filter((i) => !i.isPublished);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          i.description?.toLowerCase().includes(q) ||
          i.tags?.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [allItems, typeFilter, statusFilter, search]);

  const draftCount = allItems.filter((i) => !i.isPublished).length;
  const publishedCount = allItems.filter((i) => i.isPublished).length;

  const buildPayload = (form: FormState) => ({
    contentType: form.contentType,
    quarter: form.quarter,
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    durationMinutes: form.durationMinutes
      ? Number(form.durationMinutes)
      : undefined,
    tags: form.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
    videoUrl: isVideoType(form.contentType)
      ? form.videoUrl.trim() || undefined
      : undefined,
    thumbnailUrl: form.thumbnailUrl.trim() || undefined,
    fileUrl: form.fileUrl.trim() || undefined,
    fileName: form.fileName.trim() || undefined,
    isDownloadable: form.isDownloadable,
    isPublished: form.isPublished,
    readingItems:
      form.contentType === "reading"
        ? form.readingItems.map(({ _tempId, ...r }) => r)
        : [],
    chapterMarkers: isVideoType(form.contentType)
      ? form.chapterMarkers
          .filter((m) => m.timeSeconds && m.label)
          .map(({ _tempId, timeSeconds, label }) => ({
            timeSeconds: Number(timeSeconds),
            label,
          }))
      : [],
  });

  const handleSave = async (form: FormState) => {
    setFormError(null);
    try {
      if (editTarget) {
        await updateContent({
          id: editTarget.id,
          ...buildPayload(form),
        }).unwrap();
      } else {
        await createContent(buildPayload(form) as any).unwrap();
      }
      setShowForm(false);
      setEditTarget(null);
    } catch (err: any) {
      setFormError(
        err?.data?.error ?? "Something went wrong. Please try again.",
      );
    }
  };

  const handleTogglePublish = async (item: LibraryContent) => {
    try {
      await updateContent({
        id: item.id,
        isPublished: !item.isPublished,
      }).unwrap();
    } catch {
      /* swallow — RTK query will show the error in the cache */
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteContent(deleteTarget.id).unwrap();
      setDeleteTarget(null);
    } catch {
      /* swallow */
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
            Content Management
          </p>
          <h1 className="text-2xl font-bold text-gray-900">Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Keynotes, panels, reading lists, resources, and task briefs.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditTarget(null);
              setFormError(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <Plus size={16} /> Add content
          </button>
        )}
      </div>

      {/* SUMMARY TILES */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total",
            value: allItems.length,
            colour: "text-purple-700",
            icon: <BookOpen size={12} className="text-purple-400" />,
          },
          {
            label: "Published",
            value: publishedCount,
            colour: "text-green-700",
            icon: <Eye size={12} className="text-green-400" />,
          },
          {
            label: "Drafts",
            value: draftCount,
            colour: draftCount > 0 ? "text-amber-600" : "text-gray-400",
            icon: <EyeOff size={12} className="text-amber-400" />,
          },
          {
            label: "Filtered",
            value: filtered.length,
            colour: "text-blue-700",
            icon: <Clock size={12} className="text-blue-400" />,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4"
          >
            <div className="flex items-center gap-1.5 mb-1 text-[11px] text-gray-400">
              {s.icon}
              {s.label}
            </div>
            {isLoading ? (
              <div className="h-7 w-12 bg-gray-100 animate-pulse rounded-lg mt-1" />
            ) : (
              <p className={`text-2xl font-bold ${s.colour}`}>{s.value}</p>
            )}
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search title, description, tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
          />
        </div>
        <select
          value={quarterFilter}
          onChange={(e) =>
            setQuarterFilter(e.target.value as QuarterKey | "all")
          }
          className="text-sm px-3 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300"
        >
          <option value="all">All quarters</option>
          {QUARTERS.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ContentType | "all")}
          className="text-sm px-3 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300"
        >
          <option value="all">All types</option>
          {CONTENT_TYPE_META.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
          {(["all", "published", "draft"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                ${statusFilter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* GRID */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-gray-100 animate-pulse h-64"
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
            <BookOpen size={22} className="text-gray-300" />
          </div>
          <p className="text-sm font-semibold text-gray-600">
            {search ? "No results for this search" : "No content yet"}
          </p>
          <p className="text-xs text-gray-400 max-w-xs">
            {search
              ? "Try a different search term or clear the filter."
              : canEdit
                ? `Click "Add content" to create the first library item.`
                : "No library items have been published yet."}
          </p>
          {canEdit && !search && (
            <button
              onClick={() => {
                setEditTarget(null);
                setFormError(null);
                setShowForm(true);
              }}
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:text-purple-700"
            >
              <Plus size={14} /> Add content
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-[11px] text-gray-400">
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((item) => (
              <ContentCard
                key={item.id}
                item={item}
                canEdit={canEdit}
                onEdit={(i) => {
                  setEditTarget(i);
                  setFormError(null);
                  setShowForm(true);
                }}
                onDelete={setDeleteTarget}
                onTogglePublish={handleTogglePublish}
              />
            ))}
          </div>
        </>
      )}

      {/* FORM SLIDE-IN PANEL */}
      {showForm && canEdit && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="flex-1 bg-black/40"
            onClick={() => {
              setShowForm(false);
              setEditTarget(null);
            }}
          />
          <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editTarget ? "Edit content" : "Add library content"}
                </h2>
                <p className="text-xs text-gray-400">
                  {editTarget ? editTarget.title : "Fill in the details below."}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <ContentForm
                initial={editTarget ? itemToForm(editTarget) : EMPTY_FORM}
                isEditing={!!editTarget}
                isSaving={isCreating || isUpdating}
                apiError={formError}
                onSave={handleSave}
                onCancel={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteTarget && (
        <DeleteConfirm
          item={deleteTarget}
          isLoading={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminLibrary;
